import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, AlertCircle, MapPin, CarFront, ExternalLink, Droplets, Zap } from "lucide-react";
import { STUDIO_ADDRESS } from "../../lib/services";

export default function StepVehicle({ vehicleType, setVehicleType, vehicleBrandModel, setVehicleBrandModel, notes, setNotes,
  locationType, setLocationType, serviceAddress, setServiceAddress, addressNote, setAddressNote,
  utilitiesConfirmed, setUtilitiesConfirmed, user, guestForm, setGuestForm, vehicleTypes, mobileExtra,
  showErrors, guestPasswordOk, guestPhoneOk }) {
  const carModelMissing = showErrors && !vehicleBrandModel.trim();
  const locationInvalid = showErrors && locationType === "mobile" && !(serviceAddress.trim().length > 3 && utilitiesConfirmed);
  const guestFirstNameMissing = showErrors && !user && !guestForm.firstName.trim();
  const guestLastNameMissing = showErrors && !user && !guestForm.lastName.trim();
  const guestEmailMissing = showErrors && !user && !guestForm.email.trim();
  const guestPasswordInvalid = showErrors && !user && !guestPasswordOk;
  const guestPhoneInvalid = showErrors && !user && !guestPhoneOk;
  return (
    <div className="space-y-5">
      <div>
        <div className="text-sm text-apex-cream/70 mb-2">Typ vozidla</div>
        <div className="grid md:grid-cols-3 gap-3">
          {vehicleTypes.map(v => {
            const sel = vehicleType === v.id;
            return (
              <button key={v.id} onClick={() => setVehicleType(v.id)} className={`text-left rounded-xl p-4 border transition ${
                sel ? "bg-apex-gold/10 border-apex-gold" : "bg-apex-card border-[#bababa]/30 hover:border-apex-gold/40"
              }`} data-testid={`booking-vehicle-${v.id}`}>
                <div className="text-apex-cream font-semibold">{v.label}</div>
                <div className="text-xs text-apex-cream/60">{v.surcharge > 0 ? `Příplatek +${Math.round(v.surcharge * 100)}%` : "Bez příplatku"}</div>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="block">
          <span className="text-sm text-apex-cream/75 mb-1.5 inline-block">Značka a model vozu <span className="text-apex-gold">*</span></span>
          <input
            value={vehicleBrandModel}
            onChange={(e) => setVehicleBrandModel(e.target.value)}
            placeholder="např. BMW M3 2023"
            className={`w-full px-4 py-3 rounded-xl bg-apex-bg border focus:outline-none text-apex-cream transition-colors ${
              carModelMissing ? "border-red-500 focus:border-red-500" : "border-apex-line focus:border-apex-gold"
            }`}
            data-testid="booking-car-input"
          />
          {carModelMissing && (
            <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1.5" data-testid="booking-car-error">
              <AlertCircle className="w-3.5 h-3.5" /> Vyplňte prosím značku a model vozu.
            </p>
          )}
        </label>
      </div>

      {/* Místo konání služby */}
      <div>
        <div className="text-sm text-apex-cream/70 mb-2">Kde má čištění proběhnout?</div>
        <div className="grid md:grid-cols-2 gap-3">
          <button
            onClick={() => setLocationType("studio")}
            className={`relative text-left rounded-2xl p-4 border transition-all ${
              locationType === "studio" ? "bg-apex-gold/10 border-apex-gold" : "bg-apex-card border-apex-line hover:border-apex-gold/40"
            }`}
            data-testid="booking-location-studio"
          >
            <div className="flex items-start gap-3">
              <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${locationType === "studio" ? "bg-apex-gold text-apex-bg" : "bg-apex-gold/15 text-apex-gold"}`}>
                <CarFront className="w-5 h-5" />
              </span>
              <span>
                <span className="block text-apex-cream font-semibold">Přivezu vůz k vám</span>
                <span className="block text-xs text-apex-cream/60 mt-0.5">{STUDIO_ADDRESS.full} · bez příplatku</span>
                <a
                  href={STUDIO_ADDRESS.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-xs text-apex-gold hover:underline mt-1.5"
                  data-testid="booking-location-maps-link"
                >
                  Zobrazit na Google Mapách <ExternalLink className="w-3 h-3" />
                </a>
              </span>
            </div>
          </button>
          <button
            onClick={() => setLocationType("mobile")}
            className={`relative text-left rounded-2xl p-4 pt-9 sm:pt-4 border transition-all ${
              locationType === "mobile" ? "bg-apex-gold/10 border-apex-gold" : "bg-apex-card border-apex-line hover:border-apex-gold/40"
            }`}
            data-testid="booking-location-mobile"
          >
            <span className="absolute top-3 left-3 sm:left-auto sm:right-3 text-[10px] font-bold px-2 py-0.5 rounded-full bg-apex-gold text-apex-bg whitespace-nowrap">Do 25 km v ceně</span>
            <div className="flex items-start gap-3">
              <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${locationType === "mobile" ? "bg-apex-gold text-apex-bg" : "bg-apex-gold/15 text-apex-gold"}`}>
                <MapPin className="w-5 h-5" />
              </span>
              <span className="min-w-0">
                <span className="block text-apex-cream font-semibold pr-1 break-words">Mobilní služba — přijedeme k vám</span>
                <span className="block text-xs text-apex-cream/60 mt-0.5">Domů či do práce · do 25 km od Zelenče</span>
              </span>
            </div>
          </button>
        </div>

        <AnimatePresence>
          {locationType === "mobile" && (
            <motion.div
              initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="mb-3 rounded-xl bg-apex-bg border border-apex-line p-3 text-xs text-apex-cream/70" data-testid="booking-travel-fee-note">
                Dojezd v ceně služby do 25 km od Zelenče při objednávce nad 2 400 Kč. U menší objednávky dojezd +{mobileExtra} Kč. Delší vzdálenost řešíme po dohodě.
              </div>
              <div className={`mt-3 rounded-2xl border p-4 space-y-3 ${locationInvalid ? "border-red-500/50 bg-red-500/5" : "border-apex-gold/30 bg-apex-gold/5"}`} data-testid="booking-mobile-fields">
                <label className="block">
                  <span className="text-sm text-apex-cream/75 mb-1.5 inline-block">Adresa, kam máme přijet <span className="text-apex-gold">*</span></span>
                  <input
                    value={serviceAddress}
                    onChange={(e) => setServiceAddress(e.target.value)}
                    placeholder="Ulice a č.p., město, PSČ"
                    className={`w-full px-4 py-3 rounded-xl bg-apex-bg border focus:outline-none text-apex-cream transition-colors ${
                      showErrors && serviceAddress.trim().length <= 3 ? "border-red-500 focus:border-red-500" : "border-apex-line focus:border-apex-gold"
                    }`}
                    data-testid="booking-address-input"
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-apex-cream/75 mb-1.5 inline-block">Upřesnění místa (volitelné)</span>
                  <input
                    value={addressNote}
                    onChange={(e) => setAddressNote(e.target.value)}
                    placeholder="např. uvnitř areálu, před vraty, parkování ve dvoře…"
                    className="w-full px-4 py-3 rounded-xl bg-apex-bg border border-apex-line focus:border-apex-gold focus:outline-none text-apex-cream"
                    data-testid="booking-address-note-input"
                  />
                </label>
                <label className="flex items-start gap-3 cursor-pointer rounded-xl bg-apex-bg border border-apex-line p-3.5 hover:border-apex-gold/40 transition" data-testid="booking-utilities-checkbox">
                  <span className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${utilitiesConfirmed ? "bg-apex-gold border-apex-gold" : "border-apex-gold/50"}`}>
                    {utilitiesConfirmed && <Check className="w-3.5 h-3.5 text-apex-bg" />}
                  </span>
                  <input type="checkbox" checked={utilitiesConfirmed} onChange={(e) => setUtilitiesConfirmed(e.target.checked)} className="sr-only" />
                  <span className="text-sm text-apex-cream/85">
                    Potvrzuji, že na místě bude k dispozici <span className="text-apex-gold font-semibold inline-flex items-center gap-1"><Droplets className="w-3.5 h-3.5" /> voda</span> a <span className="text-apex-gold font-semibold inline-flex items-center gap-1"><Zap className="w-3.5 h-3.5" /> elektřina</span>. <span className="text-apex-cream/55">(Bez nich nelze mobilní službu provést.)</span>
                  </span>
                </label>
                {!(serviceAddress.trim().length > 3 && utilitiesConfirmed) && (
                  <p className={`text-xs flex items-center gap-1.5 ${locationInvalid ? "text-red-400" : "text-apex-gold/80"}`} data-testid="booking-mobile-hint">
                    <AlertCircle className="w-3.5 h-3.5" /> Pro pokračování vyplňte adresu a potvrďte přístup k vodě a elektřině.
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div>
        <label className="block">
          <span className="text-sm text-apex-cream/75 mb-1.5 inline-block">Poznámka (volitelné)</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Cokoli, co bychom měli vědět" className="w-full px-4 py-3 rounded-xl bg-apex-bg border border-apex-line focus:border-apex-gold focus:outline-none text-apex-cream" data-testid="booking-notes-input" />
        </label>
      </div>

      {!user && (
        <div className="rounded-2xl border border-apex-gold/30 bg-apex-gold/5 p-5">
          <div className="flex items-center gap-2 text-apex-gold font-semibold mb-3"><AlertCircle className="w-4 h-4" /> Vaše kontaktní údaje (vytvoříme vám účet)</div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <input value={guestForm.firstName} onChange={(e) => setGuestForm({ ...guestForm, firstName: e.target.value })} placeholder="Jméno" className={`w-full px-4 py-3 rounded-xl bg-apex-bg border text-apex-cream transition-colors ${guestFirstNameMissing ? "border-red-500" : "border-apex-line"}`} data-testid="guest-firstname" />
              {guestFirstNameMissing && <p className="mt-1 text-xs text-red-400">Vyplňte jméno.</p>}
            </div>
            <div>
              <input value={guestForm.lastName} onChange={(e) => setGuestForm({ ...guestForm, lastName: e.target.value })} placeholder="Příjmení" className={`w-full px-4 py-3 rounded-xl bg-apex-bg border text-apex-cream transition-colors ${guestLastNameMissing ? "border-red-500" : "border-apex-line"}`} data-testid="guest-lastname" />
              {guestLastNameMissing && <p className="mt-1 text-xs text-red-400">Vyplňte příjmení.</p>}
            </div>
            <div>
              <input value={guestForm.email} type="email" onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })} placeholder="Email" className={`w-full px-4 py-3 rounded-xl bg-apex-bg border text-apex-cream transition-colors ${guestEmailMissing ? "border-red-500" : "border-apex-line"}`} data-testid="guest-email" />
              {guestEmailMissing && <p className="mt-1 text-xs text-red-400">Vyplňte e-mail.</p>}
            </div>
            <div>
              <input value={guestForm.phone} type="tel" onChange={(e) => setGuestForm({ ...guestForm, phone: e.target.value })} placeholder="Telefon" className={`w-full px-4 py-3 rounded-xl bg-apex-bg border text-apex-cream transition-colors ${guestPhoneInvalid ? "border-red-500" : "border-apex-line"}`} data-testid="guest-phone" />
              {guestPhoneInvalid && <p className="mt-1 text-xs text-red-400">Vyplňte telefon — potřebujeme ho pro domluvení termínu.</p>}
            </div>
            <div className="md:col-span-2">
              <input value={guestForm.password} type="password" onChange={(e) => setGuestForm({ ...guestForm, password: e.target.value })} placeholder="Heslo pro nový účet (min. 6 znaků)" className={`w-full px-4 py-3 rounded-xl bg-apex-bg border text-apex-cream transition-colors ${guestPasswordInvalid ? "border-red-500" : "border-apex-line"}`} data-testid="guest-password" />
              {guestPasswordInvalid && (
                <p className="mt-1 text-xs text-red-400" data-testid="guest-password-error">
                  {guestForm.password ? "Heslo musí mít alespoň 6 znaků." : "Zadejte heslo (alespoň 6 znaků)."}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
